<template>
    <section class="absence-gantt">
        <v-row class="ma-0 mb-2">
            <v-col cols="12" md="4">
                <v-date-input 
                    v-model="filters.date_range"
                    label="Datas"
                    :disabled="isInitialRequestsLoading"
                    :loading="isLoadingRequests"
                    hide-details
                    prepend-icon=""
                    multiple="range"
                    auto-complete="off"
                />
            </v-col>

            <!-- <v-col cols="12" md="6">
                <v-select
                    v-model="filters.month"
                    label="Mês"
                    :items="monthOptions"
                    item-title="title"
                    item-value="value"
                    :disabled="isLoadingRequests"
                    hide-details
                />
            </v-col> -->

            <v-col cols="12" md="4">
                <v-autocomplete
                    v-model="filters.department_ids"
                    label="Centro de custo"
                    :items="departments"
                    :item-title="departmentLabel"
                    item-value="id"
                    :loading="isLoadingDepartments"
                    :disabled="isLoadingDepartments"
                    clearable
                    hide-details
                    auto-complete="off"
                    multiple
                />
            </v-col>

            <v-col cols="12" md="4" >
                <v-autocomplete
                    v-model="filters.user_ids"
                    label="Colaborador(a)"
                    :items="availableUsers"
                    :item-title="employeeLabel"
                    item-value="id"
                    :loading="isLoadingUsers"
                    :disabled="isInitialUsersLoading"
                    clearable
                    hide-details
                    multiple
                />
            </v-col>

            <v-col cols="12" class="d-flex align-center justify-end">
                <v-btn
                    :prepend-icon="
                        isInitialRequestsLoading
                        ? 'mdi-loading mdi-spin'
                        : 'mdi-filter'
                    "
                    color="primary"
                    @click="applyTimelineFilters"
                    :disabled="isInitialRequestsLoading || !hasDraftTimelineFilters"
                >
                    {{ hasDraftTimelineFilters ? "Filtrar" : "Escolha CC ou Colaborador(a)" }}
                </v-btn>
            </v-col>

            <!-- <v-col cols="12" sm="6" lg="2">
                <v-autocomplete
                    v-model="filters.project_id"
                    label="Obra"
                    :items="projectOptions"
                    :item-title="projectLabel"
                    item-value="id"
                    :disabled="isLoading || !projectOptions.length"
                    :hint="projectOptions.length ? undefined : 'Sem obras disponíveis nos dados atuais'"
                    persistent-hint
                    clearable
                    hide-details="auto"
                />
            </v-col> -->
        </v-row>

        <div v-if="!hasAppliedTimelineFilters">
            <p class="text-h6 text-center my-10">
                Selecione pelo menos um Centro de Custo ou Colaborador(a) e clique em Filtrar para carregar o mapa de férias
            </p>
        </div>

        <div v-else>

            <div class="d-flex flex-wrap align-center ga-4 mb-4 text-body-2">
                <span class="d-flex align-center ga-2">
                    <span class="legend-swatch bg-success"></span>
                    Processado
                </span>
                <span class="d-flex align-center ga-2">
                    <span class="legend-swatch bg-info"></span>
                    Aprovado por processar
                </span>
                <span class="d-flex align-center ga-2">
                    <span class="legend-swatch bg-warning"></span>
                    Pendente de aprovação
                </span>
            </div>

            <AbsenceRequestTimeline
                :days="timelineDays"
                :collaborators="timelineCollaborators"
                :requests="filteredRequests"
                :loading="isLoading"
                :error="hasRequestError"
                :approved-days-year="currentYear"
            />
        </div>

    </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import AbsenceRequest from "@/api/absences/requests/AbsenceRequests.js";
import Department from "@/api/departments/Department.js";
import User from "@/api/User.js";
import AbsenceRequestTimeline from "./AbsenceRequestTimeline.vue";
import { getDate } from "../../../composables/datetime.js";

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth();
const currentMonthStartDay = 1;
const currentMonthEnd = getDate(new Date(currentYear, currentMonth + 1, 0));
const currentMonthStart = getDate(new Date(currentYear, currentMonth, currentMonthStartDay));
const maximumTimelineDays = 366;

const filters = ref({
    year: currentYear,
    month: currentMonth,
    date_range: [
        currentMonthStart,
        currentMonthEnd,
    ],
    department_ids: null,
    user_ids: null,
    project_id: null,
    per_page: 25,
});
const appliedFilters = ref({
    date_range: [...filters.value.date_range],
    department_ids: [],
    user_ids: [],
    project_id: null,
});
const absenceRequests = ref([]);
const absenceRequestIds = new Set();
const availableUsers = ref([]);
const timelineUsers = ref([]);
const departments = ref([]);
const isLoadingRequests = ref(false);
const isInitialRequestsLoading = ref(false);
const isLoadingUsers = ref(false);
const isInitialUsersLoading = ref(false);
const isLoadingTimelineUsers = ref(false);
const isLoadingDepartments = ref(false);
const hasRequestError = ref(false);
let timelineGeneration = 0;
let activeUserRequestController = null;
let activeTimelineUserRequestController = null;
let activeAbsenceRequestController = null;
let restartTimeout = null;
let isUnmounted = false;

const monthOptions = [
    { title: "Todos os meses", value: null },
    { title: "Janeiro", value: 0 },
    { title: "Fevereiro", value: 1 },
    { title: "Março", value: 2 },
    { title: "Abril", value: 3 },
    { title: "Maio", value: 4 },
    { title: "Junho", value: 5 },
    { title: "Julho", value: 6 },
    { title: "Agosto", value: 7 },
    { title: "Setembro", value: 8 },
    { title: "Outubro", value: 9 },
    { title: "Novembro", value: 10 },
    { title: "Dezembro", value: 11 },
];

const yearOptions = computed(() => {
    const firstYear = Math.min(2000, currentYear);

    return Array.from(
        { length: currentYear + 2 - firstYear + 1 },
        (_, index) => currentYear + 2 - index,
    );
});

const isLoading = computed(() => {
    return isInitialRequestsLoading.value
        || isInitialUsersLoading.value
        || isLoadingDepartments.value;
});

const hasDraftTimelineFilters = computed(() => {
    return normaliseIds(filters.value.department_ids).length > 0
        || normaliseIds(filters.value.user_ids).length > 0;
});

const hasAppliedTimelineFilters = computed(() => {
    return normaliseIds(appliedFilters.value.department_ids).length > 0
        || normaliseIds(appliedFilters.value.user_ids).length > 0;
});

const projectOptions = computed(() => {
    const projects = new Map();

    [...availableUsers.value, ...absenceRequests.value].forEach((record) => {
        const project = getProject(record);

        if (project?.id) {
            projects.set(project.id, project);
        }
    });

    return [...projects.values()];
});

const timelineDays = computed(() => {
    const [startDate, endDate] = getDateRange(appliedFilters.value.date_range);
    const maximumEndDate = startDate
        ? formatDateKey(getMaximumTimelineEndDate(startDate))
        : null;
    const boundedEndDate = maximumEndDate && endDate > maximumEndDate
        ? maximumEndDate
        : endDate;

    if (!startDate || !boundedEndDate || startDate > boundedEndDate) {
        return [];
    }

    const days = [];
    const date = new Date(`${startDate}T00:00:00`);
    const lastDate = new Date(`${boundedEndDate}T00:00:00`);

    while (date <= lastDate) {
        days.push({
            key: formatDateKey(date),
            date: new Date(date),
            day: date.getDate(),
            weekday: date.toLocaleDateString("pt-PT", { weekday: "short" }),
            month: date.toLocaleDateString("pt-PT", { month: "long" }),
            isWeekend: [0, 6].includes(date.getDay()),
        });
        date.setDate(date.getDate() + 1);
    }

    return days;
});

const filteredRequests = computed(() => {
    return absenceRequests.value.filter((request) => {
        const project = getProject(request);

        return isTimelineRequest(request)
            && (!appliedFilters.value.project_id || project?.id === appliedFilters.value.project_id);
    });
});

const timelineCollaborators = computed(() => {
    const selectedUserIds = normaliseIds(appliedFilters.value.user_ids);
    const selectedDepartmentIds = normaliseIds(appliedFilters.value.department_ids);
    const userMatchesDepartments = (user) => {
        if (!selectedDepartmentIds.length) {
            return true;
        }

        return (user?.departments ?? []).some((department) => {
            return selectedDepartmentIds.includes(department?.id);
        });
    };

    if (selectedUserIds.length) {
        return timelineUsers.value
            .filter((user) => {
                return selectedUserIds.includes(user?.id)
                    && userMatchesDepartments(user);
            })
            .sort((firstUser, secondUser) => employeeLabel(firstUser).localeCompare(employeeLabel(secondUser), "pt-PT"));
    }

    const collaborators = new Map();
    const requestedUserIds = new Set(
        filteredRequests.value
            .map((request) => request?.user_id ?? request?.user?.id)
            .filter(Boolean),
    );

    timelineUsers.value.forEach((user) => {
        const project = getProject(user);
        const belongsToSelectedProject = !appliedFilters.value.project_id
            || project?.id === appliedFilters.value.project_id;

        if (belongsToSelectedProject && userMatchesDepartments(user)) {
            collaborators.set(user.id, user);
        }
    });

    filteredRequests.value.forEach((request) => {
        const user = request?.user;
        const userId = request?.user_id ?? user?.id;

        if (
            userId
            && requestedUserIds.has(userId)
            && userMatchesDepartments(user)
            && !collaborators.has(userId)
        ) {
            collaborators.set(userId, user ?? { id: userId });
        }
    });

    return [...collaborators.values()]
        .sort((firstUser, secondUser) => employeeLabel(firstUser).localeCompare(employeeLabel(secondUser), "pt-PT"));
});

function departmentLabel(department) {
    const code = department?.external_key ? `${department.external_key} - ` : "";

    return `${code}${department?.name ?? "Centro de custo sem nome"}`;
}

function employeeLabel(user) {
    const code = user?.worker_code ? `${user.worker_code} - ` : "";

    return `${code}${user?.name ?? user?.email ?? "Colaborador sem nome"}`;
}

function projectLabel(project) {
    const code = project?.code ?? project?.external_key;

    return `${code ? `${code} - ` : ""}${project?.name ?? "Obra sem nome"}`;
}

function getProject(record) {
    const project = record?.project
        ?? record?.construction_project
        ?? record?.constructionProject
        ?? record?.user?.project
        ?? record?.user?.construction_project
        ?? record?.user?.constructionProject;
    const projectId = record?.project_id
        ?? record?.construction_project_id
        ?? record?.user?.project_id
        ?? record?.user?.construction_project_id
        ?? project?.id;

    if (!projectId) {
        return null;
    }

    return {
        id: projectId,
        code: project?.code ?? project?.external_key,
        name: project?.name ?? record?.project_name ?? record?.construction_project_name,
    };
}

function isTimelineRequest(request) {
    const status = request?.status?.status ?? request?.status;

    return ["pending", "approved", "processed"].includes(status)
        && !!request?.start_date
        && !!request?.end_date;
}

function formatDateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
}

function extractResponseList(response) {
    const responseData = response?.data;

    if (Array.isArray(responseData?.data)) {
        return responseData.data;
    }

    if (Array.isArray(responseData?.data?.data)) {
        return responseData.data.data;
    }

    return Array.isArray(responseData) ? responseData : [];
}

async function fetchAbsenceRequests(
    reset = false,
    userIds = [],
    generation = timelineGeneration,
) {
    const selectedUserIds = normaliseIds(appliedFilters.value.user_ids);
    const requestUserIds = selectedUserIds.length
        ? normaliseIds(userIds).filter((userId) => selectedUserIds.includes(userId))
        : normaliseIds(userIds);

    if (!requestUserIds.length) {
        if (generation === timelineGeneration) {
            isLoadingRequests.value = false;
            isInitialRequestsLoading.value = false;
        }

        return;
    }

    isLoadingRequests.value = true;
    isInitialRequestsLoading.value = reset;

    const [startDate, endDate] = getDateRange(appliedFilters.value.date_range);
    const requestController = new AbortController();
    activeAbsenceRequestController = requestController;
    let page = 1;
    let lastPage = 1;

    try {
        do {
            if (generation !== timelineGeneration) {
                return;
            }

            const response = await AbsenceRequest.all(
                {
                    page,
                    per_page: filters.value.per_page,
                    start_date: startDate,
                    end_date: endDate,
                    user_ids: requestUserIds,
                },
                {
                    signal: requestController.signal,
                },
            );
            const responseData = response?.data ?? {};

            if (generation !== timelineGeneration) {
                return;
            }

            mergeAbsenceRequests(extractResponseList(response));
            page += 1;
            lastPage = Number(responseData?.last_page ?? 1);
            isInitialRequestsLoading.value = false;
        } while (page <= lastPage);
    } catch (error) {
        if (generation !== timelineGeneration || isCancellationError(error)) {
            return;
        }

        hasRequestError.value = true;
        console.error("Não foi possível carregar o mapa de ausências:", error);
    } finally {
        if (activeAbsenceRequestController === requestController) {
            activeAbsenceRequestController = null;
        }

        if (generation !== timelineGeneration) {
            return;
        }

        isLoadingRequests.value = false;
        isInitialRequestsLoading.value = false;
    }
}

function mergeAbsenceRequests(newRequests) {
    absenceRequests.value.push(
        ...newRequests.filter((request) => {
            if (!request?.id || absenceRequestIds.has(request.id)) {
                return false;
            }

            absenceRequestIds.add(request.id);

            return true;
        }),
    );
}

function normaliseIds(ids) {
    return (Array.isArray(ids) ? ids : [ids])
        .map((id) => typeof id === "object" ? id?.id : id)
        .filter(Boolean);
}

function normaliseDate(date) {
    return date instanceof Date ? getDate(date) : date ?? null;
}

function getDateRange(dateRange) {
    const dates = (Array.isArray(dateRange) ? dateRange : [dateRange])
        .map(normaliseDate)
        .filter(Boolean)
        .sort();

    if (!dates.length) {
        return [];
    }

    return [dates[0], dates.at(-1)];
}

function getMaximumTimelineEndDate(startDate) {
    const maximumEndDate = new Date(`${startDate}T00:00:00`);

    maximumEndDate.setDate(
        maximumEndDate.getDate() + maximumTimelineDays - 1,
    );

    return maximumEndDate;
}

function restrictDateRangeToMaximumDays() {
    const [startDate, endDate] = getDateRange(filters.value.date_range);

    if (!startDate || !endDate) {
        return;
    }

    const maximumEndDate = getMaximumTimelineEndDate(startDate);

    if (new Date(`${endDate}T00:00:00`) <= maximumEndDate) {
        return;
    }

    filters.value.date_range = createDateRangeValues(
        startDate,
        formatDateKey(maximumEndDate),
    );
}

function createDateRangeValues(startDate, endDate) {
    const dates = [];
    const date = new Date(`${startDate}T00:00:00`);
    const lastDate = new Date(`${endDate}T00:00:00`);

    while (date <= lastDate) {
        dates.push(new Date(date));
        date.setDate(date.getDate() + 1);
    }

    return dates;
}

function isCancellationError(error) {
    return error?.code === "ERR_CANCELED"
        || error?.name === "CanceledError";
}

async function fetchAvailableUsers(reset = false) {
    if (isUnmounted || isLoadingUsers.value) {
        return;
    }

    isLoadingUsers.value = true;
    isInitialUsersLoading.value = reset;

    const requestController = new AbortController();

    activeUserRequestController = requestController;

    try {
        const response = await User.list(
            {
                with_absence_year_summary: 1,
                absence_year_summary_year: currentYear,
                with_departments: 1,
                without_pending_membership_requests: 0,
                domain: "list__AbsenceRequests",
            },
            {
                signal: requestController.signal,
            },
        );

        const newUsers = extractResponseList(response);
        const usersById = new Map(
            (reset ? [] : availableUsers.value).map((user) => [user.id, user]),
        );

        newUsers.forEach((user) => {
            usersById.set(user.id, user);
        });

        availableUsers.value = [...usersById.values()];

        if (!hasAppliedTimelineFilters.value) {
            const timelineUsersById = new Map(
                timelineUsers.value.map((user) => [user.id, user]),
            );

            newUsers.forEach((user) => {
                timelineUsersById.set(user.id, user);
            });

            timelineUsers.value = [...timelineUsersById.values()];
        }

    } catch (error) {
        if (reset) {
            availableUsers.value = [];
        }
        if (!isCancellationError(error)) {
            console.error("Não foi possível carregar os colaboradores:", error);
        }
    } finally {
        if (activeUserRequestController === requestController) {
            activeUserRequestController = null;
        }

        isLoadingUsers.value = false;
        isInitialUsersLoading.value = false;

        if (isUnmounted) {
            return;
        }
    }

}

async function fetchUsers(reset = false, generation = timelineGeneration) {
    if (isUnmounted || isLoadingTimelineUsers.value) {
        return;
    }

    if (reset) {
        timelineUsers.value = [];
    }

    isLoadingTimelineUsers.value = true;

    const requestController = new AbortController();

    activeTimelineUserRequestController = requestController;

    try {
        const response = await User.list(
            {
                department_ids: appliedFilters.value.department_ids,
                with_absence_year_summary: 1,
                absence_year_summary_year: currentYear,
                with_departments: 1,
                without_pending_membership_requests: 0,
            },
            {
                signal: requestController.signal,
            },
        );

        if (generation !== timelineGeneration) {
            return;
        }

        const newUsers = extractResponseList(response);
        const usersById = new Map(
            (reset ? [] : timelineUsers.value).map((user) => [user.id, user]),
        );

        newUsers.forEach((user) => {
            usersById.set(user.id, user);
        });

        timelineUsers.value = [...usersById.values()];

        if (reset && !newUsers.length) {
            isInitialRequestsLoading.value = false;
        }

        void fetchAbsenceRequests(reset, newUsers, generation);
    } catch (error) {
        if (generation !== timelineGeneration || isCancellationError(error)) {
            return;
        }

        hasRequestError.value = true;
        isInitialRequestsLoading.value = false;
        console.error("Não foi possível carregar os colaboradores do mapa de ausências:", error);
    } finally {
        if (activeTimelineUserRequestController === requestController) {
            activeTimelineUserRequestController = null;
        }

        if (generation !== timelineGeneration) {
            return;
        }

        isLoadingTimelineUsers.value = false;

    }
}

function applyTimelineFilters() {
    restrictDateRangeToMaximumDays();
    appliedFilters.value = {
        date_range: getDateRange(filters.value.date_range),
        department_ids: normaliseIds(filters.value.department_ids),
        user_ids: normaliseIds(filters.value.user_ids),
        project_id: filters.value.project_id,
    };

    restartTimelineLoad();
}

function restartTimelineLoad() {
    if (!hasAppliedTimelineFilters.value) {
        timelineUsers.value = [...availableUsers.value];
        isInitialRequestsLoading.value = false;

        return;
    }
    timelineGeneration += 1;
    activeTimelineUserRequestController?.abort();
    activeAbsenceRequestController?.abort();

    absenceRequests.value = [];
    absenceRequestIds.clear();
    timelineUsers.value = [];
    hasRequestError.value = false;
    isLoadingRequests.value = false;
    isInitialRequestsLoading.value = true;
    isLoadingTimelineUsers.value = false;

    const selectedUserIds = normaliseIds(appliedFilters.value.user_ids);

    if (selectedUserIds.length) {
        const selectedUsers = availableUsers.value.filter((user) => {
            return selectedUserIds.includes(user.id);
        });

        timelineUsers.value = selectedUsers;
        void fetchAbsenceRequests(
            true,
            selectedUsers,
            timelineGeneration,
        );
        return;
    }

    if (normaliseIds(appliedFilters.value.department_ids).length) {
        fetchUsers(true, timelineGeneration);
        return;
    }

    if (availableUsers.value.length) {
        timelineUsers.value = [...availableUsers.value];
        void fetchAbsenceRequests(
            true,
            availableUsers.value,
            timelineGeneration,
        );
        return;
    }

    fetchAvailableUsers(true);
}

async function fetchDepartments() {
    isLoadingDepartments.value = true;

    try {
        const response = await Department.all({ request_domain: "absence_requests" });

        departments.value = extractResponseList(response);
    } catch (error) {
        departments.value = [];
        console.error("Não foi possível carregar os centros de custo:", error);
    } finally {
        isLoadingDepartments.value = false;
    }
}

onMounted(() => {
    fetchDepartments();
    fetchAvailableUsers(true);
});

onBeforeUnmount(() => {
    isUnmounted = true;

    if (restartTimeout !== null) {
        clearTimeout(restartTimeout);
    }

    activeUserRequestController?.abort();
    activeTimelineUserRequestController?.abort();
    activeAbsenceRequestController?.abort();
});
</script>

<style scoped>
.absence-gantt {
    min-width: 0;
}

.legend-swatch {
    display: inline-block;
    width: 16px;
    height: 16px;
    border-radius: 3px;
}

.legend-swatch--processed {
    background-color: rgb(var(--v-theme-success));
}

.legend-swatch--approved {
    background-color: #ed8a19;
}

.legend-swatch--pending {
    background-color: #f2c94c;
}
</style>
